/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState, Fragment } from "react";
import { api } from "../lib/apiClient";
import { useAuth } from "../lib/AuthContext";
import { StatusBadge, PriorityBadge } from "../components/StatusBadge";
import { Camera, MessageSquare, MapPin, Trash2, Pencil } from "lucide-react";
import { fmtDateTimeIST } from "../lib/formatDateTime";
import { PhotoStrip } from "../components/PhotoStrip";
import PhotoPreview from "../components/PhotoPreview";
import PhotoUploadButton from "../components/PhotoUploadButton";
import { useMatch } from "../lib/MatchContext";
import RequireMatch from "../components/RequireMatch";
import PeopleTagger, { PeoplePills } from "../components/PeopleTagger";
import Pagination from "../components/Pagination";

// DEMO placeholder — actual match id now comes from useMatch() at runtime

export default function RaiseIssuePage() {
  const { selectedMatchId } = useMatch();
  const DEMO = selectedMatchId;
  const { user: me } = useAuth();
  const isDeptHead = me?.role === "department_head";
  const isVendor = me?.role === "vendor";
  const vendorDepts = isVendor ? (me?.vendor_departments || []) : [];

  const [locations, setLocations] = useState({});
  const [categories, setCategories] = useState({});
  const [preMatchDepts, setPreMatchDepts] = useState([]);
  const [issues, setIssues] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterSubtype, setFilterSubtype] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [photoDrag, setPhotoDrag] = useState(false);
  const [page, setPage] = useState(1);
  const PER_PAGE = 20;
  const [filterDept, setFilterDept] = useState(
    isVendor ? (vendorDepts[0] || "all") : (isDeptHead && me?.department ? me.department : "all")
  );
  const [uploading, setUploading] = useState(null);
  const [commentDrafts, setCommentDrafts] = useState({});
  const [pendingPhotos, setPendingPhotos] = useState([]); // File[] queued during create
  const [openCommentId, setOpenCommentId] = useState(null);
  const [editingId, setEditingId] = useState(null); // issue id we're editing; null = create
  const fileRefs = useRef({});

  const [form, setForm] = useState({
    location_main: "North Pavilion",
    location_sub: "",
    category: "Infrastructure",
    subtype: "",
    block: "",
    p_section: "",
    seat_numbers: "",
    details: "",
    department: "",
    priority: "medium",
    tagged_people: [],
  });
  const [blocks, setBlocks] = useState([]);
  const [pSections, setPSections] = useState([]);
  const [chairSummary, setChairSummary] = useState(null);
  const [infraSummary, setInfraSummary] = useState(null);

  const loadAll = async () => {
    if (!DEMO) return;
    const [locs, cats, deps, iss, chair, infra] = await Promise.all([
      api.get("/reference/locations"),
      api.get("/reference/issue-categories"),
      api.get(`/reference/pre-match-departments?match_id=${DEMO}`),
      api.get(`/matches/${DEMO}/issues`),
      api.get(`/matches/${DEMO}/chair-maintenance-summary`),
      api.get(`/matches/${DEMO}/infrastructure-summary`),
    ]);
    setLocations(locs.data.locations);
    setCategories(cats.data.categories);
    setPreMatchDepts(deps.data.departments);
    setIssues(iss.data);
    setChairSummary(chair.data);
    setInfraSummary(infra.data);
    const firstDept = [...deps.data.departments].sort((a, b) => a.localeCompare(b))[0] || "";
    // set defaults
    setForm((f) => ({
      ...f,
      location_sub: locs.data.locations[f.location_main]?.[0] || "",
      subtype: cats.data.categories[f.category]?.[0] || "",
      department: f.department || firstDept,
    }));
  };
  useEffect(() => { loadAll(); }, [DEMO]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // update blocks + P-sections when location_main / category changes
    if (form.category !== "Chair Maintenance" || !form.location_main) {
      setBlocks([]);
      setPSections([]);
      return;
    }
    api.get(`/reference/blocks?main=${encodeURIComponent(form.location_main)}`).then((r) => {
      setBlocks(r.data.blocks);
      setPSections(r.data.p_sections || []);
      setForm((f) => ({
        ...f,
        block: f.block || r.data.blocks[0],
        // P-section only relevant for South Pavilion — otherwise clear
        p_section: (r.data.p_sections && r.data.p_sections.length)
          ? (f.p_section || r.data.p_sections[0])
          : "",
      }));
    });
  }, [form.category, form.location_main]);

  useEffect(() => {
    // Reset sub-location when main changes
    if (locations[form.location_main]) {
      setForm((f) => ({ ...f, location_sub: locations[form.location_main][0] }));
    }
  }, [form.location_main, locations]);

  useEffect(() => {
    if (categories[form.category]) {
      setForm((f) => ({ ...f, subtype: categories[form.category][0] }));
    }
  }, [form.category, categories]);

  const create = async (e) => {
    e.preventDefault();
    if (editingId) {
      // EDIT MODE — patch existing issue with all editable fields
      const { data: updated } = await api.patch(`/issues/${editingId}`, {
        location_main: form.location_main,
        location_sub: form.location_sub,
        category: form.category,
        subtype: form.subtype,
        block: form.block,
        p_section: form.p_section,
        seat_numbers: form.seat_numbers,
        details: form.details,
        department: form.department,
        priority: form.priority,
        tagged_people: form.tagged_people,
      });
      // Upload any newly queued photos to the same issue
      if (updated?.id && pendingPhotos.length) {
        for (const file of pendingPhotos) {
          const fd = new FormData();
          fd.append("file", file);
          try {
            await api.post(`/issues/${updated.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          } catch {}
        }
      }
      cancelForm();
      loadAll();
      return;
    }
    const { data: created } = await api.post(`/matches/${DEMO}/issues`, form);
    // Auto-upload any photos queued in the create form
    if (created?.id && pendingPhotos.length) {
      for (const file of pendingPhotos) {
        const fd = new FormData();
        fd.append("file", file);
        try {
          await api.post(`/issues/${created.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        } catch {
          // continue with remaining photos even if one fails
        }
      }
    }
    setShowForm(false);
    setPendingPhotos([]);
    setForm({ ...form, details: "", seat_numbers: "", tagged_people: [] });
    loadAll();
  };

  const startEdit = (i) => {
    setEditingId(i.id);
    setForm({
      location_main: i.location_main || "North Pavilion",
      location_sub: i.location_sub || "",
      category: i.category || "Infrastructure",
      subtype: i.subtype || "",
      block: i.block || "",
      p_section: i.p_section || "",
      seat_numbers: i.seat_numbers || "",
      details: i.details || "",
      department: i.department || "",
      priority: i.priority || "medium",
      tagged_people: i.tagged_people || [],
    });
    setPendingPhotos([]);
    setShowForm(true);
    // Scroll to top so the edit form is visible
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setPendingPhotos([]);
  };

  const uploadPhoto = async (id, file) => {
    setUploading(id);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/issues/${id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      loadAll();
    } finally { setUploading(null); }
  };

  const resolve = async (id) => {
    await api.patch(`/issues/${id}`, { status: "resolved", resolution_note: "Resolved" });
    loadAll();
  };

  const removeIssue = async (id) => {
    if (!window.confirm("Delete this issue? This cannot be undone.")) return;
    await api.delete(`/issues/${id}`);
    loadAll();
  };

  const addComment = async (id) => {
    const text = commentDrafts[id];
    if (!text?.trim()) return;
    await api.post(`/issues/${id}/comments`, { text });
    setCommentDrafts((d) => ({ ...d, [id]: "" }));
    loadAll();
  };

  const filtered = issues.filter((i) => {
    if (filterCategory !== "all" && i.category !== filterCategory) return false;
    if (filterSubtype !== "all" && i.subtype !== filterSubtype) return false;
    if (filterStatus !== "all" && i.status !== filterStatus) return false;
    if (filterDept !== "all" && i.department !== filterDept) return false;
    const q = search.trim().toLowerCase();
    if (q) {
      const hay = [
        i.number, i.category, i.subtype, i.department, i.priority, i.status,
        i.location_main, i.location_sub, i.block, i.p_section, i.seat_numbers,
        i.details, i.resolution_note, i.reported_by, i.reported_by_email,
        ...(Array.isArray(i.tagged_people) ? i.tagged_people : []),
        ...(Array.isArray(i.comments) ? i.comments.map((c) => c?.text || "") : []),
      ].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // Reset to page 1 whenever the filtered result set shrinks/changes so
  // users don't land on an empty page after adjusting filters.
  useEffect(() => { setPage(1); }, [filterCategory, filterSubtype, filterStatus, filterDept, search]);
  const pageItems = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  if (!selectedMatchId) return <RequireMatch />;
  return (
    <div className="p-6 md:p-10 pb-20" data-testid="page-raise-issue">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6 border-b border-cmdBorder pb-6">
        <div>
          <div className="mc-label">Pre-Match · Issue Log</div>
          <h1 className="font-head font-black text-4xl md:text-5xl uppercase tracking-tight leading-none mt-2">Raise Issue</h1>
          <p className="text-zinc-400 mt-2 text-sm max-w-2xl">Log a defect against a specific location on the ground. Route it to the responsible department with photo evidence & comments.</p>
        </div>
        {!isVendor && (
          <button
            onClick={() => { if (showForm) { cancelForm(); } else { setEditingId(null); setShowForm(true); } }}
            className="bg-incidentRed text-white px-4 py-2 font-bold uppercase tracking-widest text-xs hover:bg-red-600"
            data-testid="issue-new-btn"
          >
            {showForm ? "Cancel" : "+ Raise New Issue"}
          </button>
        )}
        {isVendor && (
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-readyGreen border border-readyGreen/50 px-3 py-2" data-testid="issue-vendor-hint">
            Vendor · view & resolve only
          </div>
        )}
      </div>

      {showForm && !isVendor && (
        <form onSubmit={create} className="mc-panel p-5 mb-6 space-y-4" data-testid="issue-form">
          {editingId && (
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-warnAmber border border-warnAmber/50 bg-warnAmber/10 px-3 py-2">
              <Pencil className="w-3 h-3" /> Editing issue — save to apply changes
            </div>
          )}
          {/* Location */}
          <div>
            <div className="mc-label mb-2 flex items-center gap-2"><MapPin className="w-3 h-3" /> Location</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <select
                className="bg-black border border-cmdBorder p-2 text-sm"
                value={form.location_main}
                onChange={(e) => setForm({ ...form, location_main: e.target.value })}
                data-testid="issue-location-main"
              >
                {Object.keys(locations).map((k) => <option key={k}>{k}</option>)}
              </select>
              <select
                className="bg-black border border-cmdBorder p-2 text-sm"
                value={form.location_sub}
                onChange={(e) => setForm({ ...form, location_sub: e.target.value })}
                data-testid="issue-location-sub"
              >
                {(locations[form.location_main] || []).map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Category */}
          <div>
            <div className="mc-label mb-2">Issue Category</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <select
                className="bg-black border border-cmdBorder p-2 text-sm"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="issue-category"
              >
                {Object.keys(categories).map((k) => <option key={k}>{k}</option>)}
              </select>
              <select
                className="bg-black border border-cmdBorder p-2 text-sm"
                value={form.subtype}
                onChange={(e) => setForm({ ...form, subtype: e.target.value })}
                data-testid="issue-subtype"
              >
                {(categories[form.category] || []).map((s) => <option key={s}>{s}</option>)}
              </select>
              <select
                className="bg-black border border-cmdBorder p-2 text-sm"
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
              >
                {["critical", "high", "medium", "low"].map((p) => <option key={p}>{p}</option>)}
              </select>
            </div>
          </div>

          {/* Chair Maintenance specific */}
          {form.category === "Chair Maintenance" && (
            <div className="border border-cmdBorder p-3 bg-black/40">
              <div className="mc-label mb-2">Chair Details</div>
              <div className={`grid grid-cols-1 ${pSections.length ? "md:grid-cols-4" : "md:grid-cols-3"} gap-3`}>
                <div>
                  <label className="text-xs text-zinc-500">Block</label>
                  <select
                    className="w-full bg-black border border-cmdBorder p-2 text-sm mt-1"
                    value={form.block}
                    onChange={(e) => setForm({ ...form, block: e.target.value })}
                    data-testid="issue-block"
                  >
                    {blocks.map((b) => <option key={b}>{b}</option>)}
                  </select>
                </div>
                {pSections.length > 0 && (
                  <div>
                    <label className="text-xs text-zinc-500">P-Section <span className="text-zinc-600">(SP)</span></label>
                    <select
                      className="w-full bg-black border border-cmdBorder p-2 text-sm mt-1"
                      value={form.p_section}
                      onChange={(e) => setForm({ ...form, p_section: e.target.value })}
                      data-testid="issue-p-section"
                    >
                      {pSections.map((p) => <option key={p}>{p}</option>)}
                    </select>
                  </div>
                )}
                <div className={pSections.length ? "md:col-span-2" : "md:col-span-2"}>
                  <label className="text-xs text-zinc-500">Seat Number(s) — comma separated</label>
                  <input
                    className="w-full bg-black border border-cmdBorder p-2 text-sm mt-1 font-mono-live"
                    placeholder="e.g., 14, 22, 27, 41"
                    value={form.seat_numbers}
                    onChange={(e) => setForm({ ...form, seat_numbers: e.target.value })}
                    data-testid="issue-seats"
                  />
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="mc-label">Details</label>
            <textarea
              rows={3}
              className="w-full bg-black border border-cmdBorder p-2 text-sm mt-1"
              placeholder="Describe the issue (optional)…"
              value={form.details}
              onChange={(e) => setForm({ ...form, details: e.target.value })}
              data-testid="issue-details"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="mc-label">Route to Department</label>
              <select
                className="w-full bg-black border border-cmdBorder p-2 text-sm mt-1"
                value={form.department}
                onChange={(e) => setForm({ ...form, department: e.target.value })}
                data-testid="issue-department"
              >
                {[...preMatchDepts].sort((a, b) => a.localeCompare(b)).map((d) => <option key={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="mc-label">Tag Individuals (optional)</label>
              <div className="mt-1">
                <PeopleTagger
                  value={form.tagged_people}
                  onChange={(v) => setForm({ ...form, tagged_people: v })}
                  placeholder="Add names to notify / hold responsible…"
                  testidPrefix="issue-tagger"
                />
              </div>
            </div>
            <div>
              <label className="mc-label flex items-center gap-2"><Camera className="w-3 h-3" /> Photos (optional)</label>
              <div
                className={`mt-1 border-2 border-dashed p-3 flex items-center gap-2 flex-wrap transition-colors ${
                  photoDrag ? "border-readyGreen bg-readyGreen/5" : "border-cmdBorder"
                }`}
                data-testid="issue-form-photo-dropzone"
                onDragOver={(e) => {
                  if (Array.from(e.dataTransfer?.types || []).includes("Files")) {
                    e.preventDefault();
                    setPhotoDrag(true);
                  }
                }}
                onDragEnter={(e) => {
                  if (Array.from(e.dataTransfer?.types || []).includes("Files")) {
                    e.preventDefault();
                    setPhotoDrag(true);
                  }
                }}
                onDragLeave={() => setPhotoDrag(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setPhotoDrag(false);
                  const files = Array.from(e.dataTransfer?.files || []).filter((f) => f.type.startsWith("image/"));
                  if (files.length) setPendingPhotos((p) => [...p, ...files]);
                }}
              >
                <label
                  className="border border-cmdBorder hover:border-white px-3 py-2 text-xs uppercase tracking-widest cursor-pointer flex items-center gap-1"
                  data-testid="issue-form-photo-picker"
                >
                  <Camera className="w-3 h-3" /> Attach photo
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setPendingPhotos((p) => [...p, ...files]);
                      e.target.value = "";
                    }}
                  />
                </label>
                <div className="text-[10px] text-zinc-500 uppercase tracking-widest font-bold">
                  {photoDrag ? "Drop images here" : "or drag & drop images anywhere in this box"}
                </div>
                {pendingPhotos.length > 0 && (
                  <div className="w-full mt-2">
                    <PhotoPreview
                      files={pendingPhotos}
                      onRemove={(i) => setPendingPhotos((p) => p.filter((_, idx) => idx !== i))}
                      testidPrefix="issue-form-photo-preview"
                    />
                    <div className="text-[10px] text-readyGreen uppercase tracking-widest font-bold mt-2">
                      {pendingPhotos.length} queued
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button type="submit" className="bg-white text-black px-4 py-2 font-bold uppercase tracking-widest text-xs" data-testid="issue-submit">
              {editingId ? "Save Changes" : "Submit Issue"}
            </button>
            <button type="button" onClick={cancelForm} className="border border-cmdBorder px-4 py-2 text-xs uppercase tracking-widest">Cancel</button>
          </div>
        </form>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="issue-filters">
        <select
          className="bg-black border border-cmdBorder text-xs py-1.5 px-2"
          value={filterCategory}
          onChange={(e) => { setFilterCategory(e.target.value); setFilterSubtype("all"); }}
          data-testid="issue-filter-category"
        >
          <option value="all">All categories</option>
          {Object.keys(categories).sort((a, b) => a.localeCompare(b)).map((c) => <option key={c}>{c}</option>)}
        </select>
        {(() => {
          const subtypeOpts = filterCategory === "all"
            ? Array.from(new Set(Object.values(categories).flat())).sort((a, b) => a.localeCompare(b))
            : (categories[filterCategory] || []);
          if (subtypeOpts.length === 0) return null;
          return (
            <select
              className={`bg-black border text-xs py-1.5 px-2 ${filterSubtype !== "all" ? "border-readyGreen text-readyGreen" : "border-cmdBorder"}`}
              value={filterSubtype}
              onChange={(e) => setFilterSubtype(e.target.value)}
              data-testid="issue-filter-subtype"
              title="Filter by issue type"
            >
              <option value="all">All types</option>
              {subtypeOpts.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          );
        })()}
        <select
          className="bg-black border border-cmdBorder text-xs py-1.5 px-2"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          data-testid="issue-filter-status"
        >
          {["all", "open", "in_progress", "resolved"].map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
        </select>
        <select
          className={`bg-black border text-xs py-1.5 px-2 ${filterDept !== "all" ? "border-readyGreen text-readyGreen" : "border-cmdBorder"}`}
          value={filterDept}
          onChange={(e) => setFilterDept(e.target.value)}
          data-testid="issue-filter-dept"
          title={isDeptHead ? "Defaulted to your department" : "Filter by routed department"}
        >
          {!isVendor && <option value="all">All departments</option>}
          {(isVendor ? [...preMatchDepts].filter((d) => vendorDepts.includes(d)) : [...preMatchDepts])
            .sort((a, b) => a.localeCompare(b))
            .map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        {isDeptHead && filterDept === me?.department && (
          <span className="text-[10px] text-readyGreen uppercase tracking-widest font-bold self-center" data-testid="issue-dept-scope-hint">
            My Dept
          </span>
        )}
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search issue #, seat, block, details, reporter…"
          className={`bg-black border text-xs py-1.5 px-2 min-w-[220px] flex-1 md:flex-none md:w-72 ${search ? "border-readyGreen text-readyGreen placeholder:text-readyGreen/50" : "border-cmdBorder text-zinc-200 placeholder:text-zinc-500"}`}
          data-testid="issue-filter-search"
        />
        {search && (
          <button
            onClick={() => setSearch("")}
            className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white border border-cmdBorder px-2"
            data-testid="issue-filter-search-clear"
            title="Clear search"
          >
            Clear
          </button>
        )}
        <div className="ml-auto font-mono-live text-xs text-zinc-500 flex items-center">
          {filtered.length} of {issues.length} issues
        </div>
      </div>

      {/* Chair Maintenance simplified tally — shown only when the top filter is Chair Maintenance */}
      {filterCategory === "Chair Maintenance" && chairSummary && (
        <div className="mc-panel p-4 mb-4" data-testid="chair-ticker">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="mc-label">Chair Maintenance Tally</div>
            <div className="text-[10px] text-zinc-500 font-mono-live">
              Live · Individual seats expand · Frame ranges (a-b) count as 1
            </div>
          </div>
          {[
            { label: "Replace",          key: "Chair Replace",    unit: "chairs" },
            { label: "Screw Fixing",     key: "Screw Fixing",     unit: "chairs" },
            { label: "Seat No Painting", key: "Seat No Painting", unit: "chairs" },
            { label: "Frames",           key: "Frame Repair",     unit: "frames" },
          ].map(({ label, key, unit }) => {
            const row = chairSummary.by_subtype[key] || { chairs: 0, resolved_chairs: 0 };
            const raised = row.chairs || 0;
            const resolved = row.resolved_chairs || 0;
            return (
              <div key={key} className="grid grid-cols-1 md:grid-cols-[160px_1fr_1fr] items-center gap-3 py-2 border-t border-cmdBorder first:border-t-0" data-testid={`chair-row-${key.replace(/\s+/g, "-")}`}>
                <div className="text-sm font-bold uppercase tracking-widest">{label}</div>
                <div className="flex items-baseline gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500 w-16">Raised</span>
                  <span className="font-mono-live font-bold text-2xl text-warnAmber" data-testid={`chair-raised-${key.replace(/\s+/g, "-")}`}>{raised}</span>
                  <span className="text-[11px] text-zinc-500">{unit}</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500 w-16">Resolved</span>
                  <span className="font-mono-live font-bold text-2xl text-readyGreen" data-testid={`chair-resolved-${key.replace(/\s+/g, "-")}`}>{resolved}</span>
                  <span className="text-[11px] text-zinc-500">{unit}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Infrastructure tally — shown only when the top filter is Infrastructure */}
      {filterCategory === "Infrastructure" && infraSummary && (
        <div className="mc-panel p-4 mb-4" data-testid="infra-ticker">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="mc-label">Infrastructure Tally</div>
            <div className="text-[10px] text-zinc-500 font-mono-live">
              Live · Raised vs Resolved · By discipline
            </div>
          </div>
          {[
            { label: "Civil",      key: "Civil" },
            { label: "Electrical", key: "Electrical" },
            { label: "Plumbing",   key: "Plumbing" },
            { label: "Drainage",   key: "Drainage" },
          ].map(({ label, key }) => {
            const row = infraSummary.by_subtype[key] || { issues: 0, resolved_issues: 0 };
            const raised = row.issues || 0;
            const resolved = row.resolved_issues || 0;
            return (
              <div key={key} className="grid grid-cols-1 md:grid-cols-[160px_1fr_1fr] items-center gap-3 py-2 border-t border-cmdBorder first:border-t-0" data-testid={`infra-row-${key}`}>
                <div className="text-sm font-bold uppercase tracking-widest">{label}</div>
                <div className="flex items-baseline gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500 w-16">Raised</span>
                  <span className="font-mono-live font-bold text-2xl text-warnAmber" data-testid={`infra-raised-${key}`}>{raised}</span>
                  <span className="text-[11px] text-zinc-500">issues</span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500 w-16">Resolved</span>
                  <span className="font-mono-live font-bold text-2xl text-readyGreen" data-testid={`infra-resolved-${key}`}>{resolved}</span>
                  <span className="text-[11px] text-zinc-500">issues</span>
                </div>
              </div>
            );
          })}
          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr_1fr] items-center gap-3 py-2 border-t-2 border-warnAmber" data-testid="infra-row-Total">
            <div className="text-sm font-bold uppercase tracking-widest">Total</div>
            <div className="flex items-baseline gap-2">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 w-16">Raised</span>
              <span className="font-mono-live font-bold text-2xl text-warnAmber" data-testid="infra-raised-Total">{infraSummary.total_issues || 0}</span>
              <span className="text-[11px] text-zinc-500">issues</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 w-16">Resolved</span>
              <span className="font-mono-live font-bold text-2xl text-readyGreen" data-testid="infra-resolved-Total">{infraSummary.resolved_issues || 0}</span>
              <span className="text-[11px] text-zinc-500">issues</span>
            </div>
          </div>
        </div>
      )}

      <div className="mc-panel overflow-x-auto hidden md:block">
        <table className="w-full mc-table">
          <thead>
            <tr>
              <th>Issue</th>
              <th>Category</th>
              <th>Dept</th>
              <th>Priority</th>
              <th>Status</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((i) => (
              <Fragment key={i.id}>
                <tr data-testid={`issue-row-${i.id}`}>
                  <td>
                    <div className="flex items-center gap-2">
                      <span className="font-mono-live text-xs text-zinc-500">{i.number}</span>
                      <span className="font-medium">{i.location_main} · {i.location_sub}</span>
                    </div>
                    {(i.block || i.p_section) && (
                      <div className="text-xs text-warnAmber mt-0.5 font-mono-live break-words">
                        {i.p_section && `${i.p_section}`}
                        {i.block && `${i.p_section ? " · " : ""}Block ${i.block}`}
                        {i.seat_numbers ? ` · Seat No: ${String(i.seat_numbers).replace(/,\s*/g, ", ")}` : ""}
                      </div>
                    )}
                    {i.details && <div className="text-xs text-zinc-500 mt-0.5">{i.details}</div>}
                    <PhotoStrip paths={i.photos} testidPrefix={`issue-photos-${i.id}`} />
                    <div className="text-[10px] text-zinc-500 mt-1 uppercase tracking-widest">
                      Reported by <span className="text-white">{i.reported_by}</span>
                      {i.comments?.length > 0 && <> · <span className="text-white">{i.comments.length}</span> comment{i.comments.length > 1 ? "s" : ""}</>}
                    </div>
                    <div className="mt-1 flex items-center gap-2 flex-wrap text-xs">
                      <span className="mc-label">Routed to</span>
                      <span className="text-white font-medium">{i.department}</span>
                      {i.tagged_people?.length > 0 && (
                        <>
                          <span className="text-zinc-600">·</span>
                          <PeoplePills people={i.tagged_people} testidPrefix={`issue-tagged-${i.id}`} />
                        </>
                      )}
                    </div>
                  </td>
                  <td className="text-zinc-300">{i.category}{i.subtype ? <div className="text-[10px] text-zinc-500 mt-0.5">{i.subtype}</div> : null}</td>
                  <td className="text-zinc-300">{i.department}</td>
                  <td><PriorityBadge priority={i.priority} done={i.status === "resolved"} /></td>
                  <td><StatusBadge status={i.status === "resolved" ? "resolved" : "open"} kind="issue" live={i.status !== "resolved"} /></td>
                  <td className="text-right">
                    <div className="inline-flex gap-2 justify-end flex-wrap">
                      {!isVendor && (
                        <>
                          <PhotoUploadButton
                            onUpload={(file) => uploadPhoto(i.id, file)}
                            label={`Photo (${i.photos?.length || 0})`}
                            testid={`issue-upload-${i.id}`}
                          />
                          <button
                            onClick={() => setOpenCommentId(openCommentId === i.id ? null : i.id)}
                            className="flex items-center gap-1 border border-cmdBorder hover:border-white px-2 py-1 text-xs uppercase tracking-widest transition-colors"
                            data-testid={`issue-comment-toggle-${i.id}`}
                            title="Show / hide comments"
                          >
                            <MessageSquare className="w-3 h-3" /> {i.comments?.length || 0}
                          </button>
                          <button
                            onClick={() => startEdit(i)}
                            className="flex items-center gap-1 border border-cmdBorder hover:border-white px-2 py-1 text-xs uppercase tracking-widest transition-colors"
                            data-testid={`issue-edit-${i.id}`}
                            title="Edit issue"
                          >
                            <Pencil className="w-3 h-3" /> Edit
                          </button>
                        </>
                      )}
                      {i.status !== "resolved" && (
                        <button
                          onClick={() => resolve(i.id)}
                          className="border border-readyGreen text-readyGreen hover:bg-readyGreen hover:text-white px-2 py-1 text-xs uppercase tracking-widest transition-colors"
                          data-testid={`issue-resolve-${i.id}`}
                        >
                          {isVendor ? "Mark Resolved" : "Resolve"}
                        </button>
                      )}
                      {!isVendor && (
                        <button
                          onClick={() => removeIssue(i.id)}
                          className="flex items-center gap-1 border border-cmdBorder hover:border-incidentRed hover:text-incidentRed px-2 py-1 text-xs uppercase tracking-widest transition-colors"
                          data-testid={`issue-delete-${i.id}`}
                          title="Delete issue"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {openCommentId === i.id && (
                  <tr data-testid={`issue-comments-${i.id}`}>
                    <td colSpan={6} className="bg-black/40">
                      <div className="p-3 space-y-2">
                        {(i.comments || []).length === 0 && (
                          <div className="text-xs text-zinc-500">No comments yet.</div>
                        )}
                        {(i.comments || []).map((c) => (
                          <div key={c.id} className="text-xs border-l-2 border-cmdBorder pl-2">
                            <span className="text-white font-medium">{c.by}</span>
                            <span className="text-zinc-500 ml-2">{fmtDateTimeIST(c.at)}</span>
                            <div className="text-zinc-300 mt-0.5">{c.text}</div>
                          </div>
                        ))}
                        <div className="flex gap-2 pt-1">
                          <input
                            type="text"
                            placeholder="Add comment…"
                            className="flex-1 bg-black border border-cmdBorder p-2 text-xs"
                            value={commentDrafts[i.id] || ""}
                            onChange={(e) => setCommentDrafts((d) => ({ ...d, [i.id]: e.target.value }))}
                            onKeyDown={(e) => e.key === "Enter" && addComment(i.id)}
                            data-testid={`issue-comment-input-${i.id}`}
                          />
                          <button
                            onClick={() => addComment(i.id)}
                            className="flex items-center gap-1 border border-cmdBorder hover:border-white px-3 py-2 text-xs uppercase tracking-widest transition-colors"
                            data-testid={`issue-comment-btn-${i.id}`}
                          >
                            <MessageSquare className="w-3 h-3" /> Post
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="text-center text-zinc-500 py-8">No issues match your filter.</td></tr>
            )}
          </tbody>
        </table>
        <div className="px-4 pb-3">
          <Pagination
            page={page}
            perPage={PER_PAGE}
            total={filtered.length}
            onPageChange={setPage}
            testidPrefix="issue-pagination"
          />
        </div>
      </div>

      {/* Mobile: card list — replaces the wide table on small screens */}
      <div className="md:hidden space-y-3" data-testid="issue-mobile-list">
        {filtered.length === 0 && (
          <div className="mc-panel p-6 text-center text-zinc-500 text-sm">No issues match your filter.</div>
        )}
        {pageItems.map((i) => (
          <div key={i.id} className="mc-panel p-4" data-testid={`issue-card-${i.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-mono-live text-[10px] text-zinc-500">{i.number}</span>
                  <StatusBadge status={i.status === "resolved" ? "resolved" : "open"} kind="issue" live={i.status !== "resolved"} />
                  <PriorityBadge priority={i.priority} done={i.status === "resolved"} />
                </div>
                <div className="font-medium text-sm">{i.location_main} · {i.location_sub}</div>
                {(i.block || i.p_section) && (
                  <div className="text-xs text-warnAmber mt-0.5 font-mono-live break-words">
                    {i.p_section && `${i.p_section}`}
                    {i.block && `${i.p_section ? " · " : ""}Block ${i.block}`}
                    {i.seat_numbers ? ` · Seat No: ${String(i.seat_numbers).replace(/,\s*/g, ", ")}` : ""}
                  </div>
                )}
                {i.details && <div className="text-xs text-zinc-500 mt-1">{i.details}</div>}
              </div>
            </div>
            <PhotoStrip paths={i.photos} testidPrefix={`issue-photos-m-${i.id}`} />
            <div className="mt-2 flex items-center gap-2 flex-wrap text-[11px]">
              <span className="border border-cmdBorder text-zinc-300 px-2 py-1 uppercase tracking-widest">{i.category}</span>
              <span className="text-zinc-500">→</span>
              <span className="border border-cmdBorder text-white px-2 py-1 uppercase tracking-widest">{i.department}</span>
            </div>
            {i.tagged_people?.length > 0 && (
              <div className="mt-2"><PeoplePills people={i.tagged_people} testidPrefix={`issue-tagged-m-${i.id}`} /></div>
            )}
            <div className="text-[10px] text-zinc-500 mt-2 uppercase tracking-widest">
              Reported by <span className="text-white">{i.reported_by}</span>
              {i.comments?.length > 0 && <> · <span className="text-white">{i.comments.length}</span> comment{i.comments.length > 1 ? "s" : ""}</>}
            </div>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              {!isVendor && (
                <>
                  <PhotoUploadButton
                    onUpload={(file) => uploadPhoto(i.id, file)}
                    label={`Photo (${i.photos?.length || 0})`}
                    testid={`issue-upload-m-${i.id}`}
                  />
                  <button
                    onClick={() => setOpenCommentId(openCommentId === i.id ? null : i.id)}
                    className="flex items-center gap-1 border border-cmdBorder hover:border-white h-10 min-w-[44px] px-2 text-xs uppercase tracking-widest transition-colors"
                    data-testid={`issue-comment-toggle-m-${i.id}`}
                    aria-label="Comments"
                  >
                    <MessageSquare className="w-4 h-4" /> {i.comments?.length || 0}
                  </button>
                  <button
                    onClick={() => startEdit(i)}
                    className="flex items-center gap-1 border border-cmdBorder hover:border-white h-10 px-3 text-xs uppercase tracking-widest transition-colors"
                    data-testid={`issue-edit-m-${i.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                </>
              )}
              {i.status !== "resolved" && (
                <button
                  onClick={() => resolve(i.id)}
                  className="flex-1 border border-readyGreen text-readyGreen hover:bg-readyGreen hover:text-white h-10 px-3 text-xs uppercase tracking-widest transition-colors"
                  data-testid={`issue-resolve-m-${i.id}`}
                >
                  {isVendor ? "Mark Resolved" : "Resolve"}
                </button>
              )}
              {!isVendor && (
                <button
                  onClick={() => removeIssue(i.id)}
                  className="border border-cmdBorder hover:border-incidentRed hover:text-incidentRed h-10 w-10 flex items-center justify-center"
                  data-testid={`issue-delete-m-${i.id}`}
                  aria-label="Delete issue"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            {openCommentId === i.id && (
              <div className="mt-3 border-t border-cmdBorder pt-3 space-y-2" data-testid={`issue-comments-m-${i.id}`}>
                {(i.comments || []).length === 0 && (
                  <div className="text-xs text-zinc-500">No comments yet.</div>
                )}
                {(i.comments || []).map((c) => (
                  <div key={c.id} className="text-xs border-l-2 border-cmdBorder pl-2">
                    <span className="text-white font-medium">{c.by}</span>
                    <span className="text-zinc-500 ml-2">{fmtDateTimeIST(c.at)}</span>
                    <div className="text-zinc-300 mt-0.5">{c.text}</div>
                  </div>
                ))}
                <div className="flex gap-2 pt-1">
                  <input
                    type="text"
                    placeholder="Add comment…"
                    className="flex-1 bg-black border border-cmdBorder p-2 text-xs"
                    value={commentDrafts[i.id] || ""}
                    onChange={(e) => setCommentDrafts((d) => ({ ...d, [i.id]: e.target.value }))}
                    onKeyDown={(e) => e.key === "Enter" && addComment(i.id)}
                    data-testid={`issue-comment-input-m-${i.id}`}
                  />
                  <button
                    onClick={() => addComment(i.id)}
                    className="flex items-center gap-1 border border-cmdBorder hover:border-white h-10 px-3 text-xs uppercase tracking-widest transition-colors"
                    data-testid={`issue-comment-btn-m-${i.id}`}
                  >
                    <MessageSquare className="w-3 h-3" /> Post
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
        <Pagination
          page={page}
          perPage={PER_PAGE}
          total={filtered.length}
          onPageChange={setPage}
          testidPrefix="issue-pagination-mobile"
        />
      </div>
    </div>
  );
}
